import { FunctionComponent, useState } from 'react';
import { Select, MenuItem } from '@mui/material';
import Switch from 'src/shared/Switch';
import PriorityDropdown from 'src/shared/PriorityDropdown';
import { selectStyles, menuProps } from 'src/shared/PriorityDropdown/styled';
import { QuestDelegationPriority, QuestStatus } from 'src/enums/quests';
import { isAdmin } from 'src/redux/auth/service';
import {
    OutlinedActionButton,
    FinalizeButton,
    FinalizeProgress
} from '../../styled';
import {
    StyledDialog,
    ModalTitle,
    ModalContent,
    ModalActions,
    FieldRow,
    FieldLabel,
    StatusFormControl,
    SwitchWrap
} from './styled';

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: {
        priority: number;
        status: number;
        alwaysHumanReview: boolean;
    }) => void;
    isLoading: boolean;
    title?: string;
    confirmLabel?: string;
}

const STATUS_OPTIONS = [
    { value: QuestStatus.NEW, label: 'Start immediately' },
    { value: QuestStatus.ON_HOLD, label: 'Backlog' }
];

const FinalizeQuestModal: FunctionComponent<Props> = ({
    open,
    onClose,
    onConfirm,
    isLoading,
    title = 'Finalize & Create Request',
    confirmLabel = 'Finalize'
}) => {
    const admin = isAdmin();
    const [priority, setPriority] = useState<number>(
        QuestDelegationPriority.MEDIUM
    );
    const [status, setStatus] = useState<number>(QuestStatus.NEW);
    const [alwaysHumanReview, setAlwaysHumanReview] = useState<boolean>(false);

    const handleConfirm = () => {
        onConfirm({ priority, status, alwaysHumanReview });
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <ModalTitle>{title}</ModalTitle>

            <ModalContent>
                <FieldRow>
                    <FieldLabel>Priority</FieldLabel>
                    <PriorityDropdown
                        priority={priority}
                        onPriorityChange={setPriority}
                        isAdmin={admin}
                        showLabel={false}
                    />
                </FieldRow>

                <FieldRow>
                    <FieldLabel>Status</FieldLabel>
                    <StatusFormControl size="small">
                        <Select
                            value={status}
                            onChange={(e) =>
                                setStatus(e.target.value as number)
                            }
                            sx={selectStyles}
                            MenuProps={menuProps}
                            renderValue={(val) =>
                                STATUS_OPTIONS.find((o) => o.value === val)
                                    ?.label ?? ''
                            }
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </StatusFormControl>
                </FieldRow>

                <FieldRow>
                    <FieldLabel>Human review</FieldLabel>
                    <SwitchWrap>
                        <Switch
                            label=""
                            variant="simple"
                            checked={alwaysHumanReview}
                            onchange={setAlwaysHumanReview}
                        />
                    </SwitchWrap>
                </FieldRow>
            </ModalContent>

            <ModalActions>
                <OutlinedActionButton variant="contained" onClick={onClose}>
                    Cancel
                </OutlinedActionButton>
                <FinalizeButton
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={isLoading}
                >
                    {isLoading ? <FinalizeProgress size={20} /> : confirmLabel}
                </FinalizeButton>
            </ModalActions>
        </StyledDialog>
    );
};

export default FinalizeQuestModal;
